<template>
	<div
		id="logsView"
		:class="{ animate: animateView }"
		:style="{ 'animation-delay': animationDelay }"
		class="content-container"
	>
		<section id="logs-detail" :class="{ animate: animate }" class="section-container">
			<div style="height: 52px; overflow: hidden">
				<div class="section-header clipped-medium-backward-events-logs">
					<img src="/icons/conversation.svg" />
					<h1>MISSION LOG</h1>
				</div>
				<div class="rhombus-back">&nbsp;</div>
			</div>
			<div class="section-content-container extra-margins">
				<div ref="detail" class="log-detail" v-if="selectedMission">
					<button class="dossier-back" @click="selectedSlug = null">◀ CAMPAIGN OVERVIEW</button>
					<vue-markdown-it
						:source="selectedMission.content"
						preset="default"
						:options="{ html: true }"
						class="markdown"
					/>
					<div class="recap-section" v-if="selectedRecap">
						<div class="recap-header">
							<img src="/icons/clock.svg" class="recap-icon" />
							<span>Mission Recap</span>
						</div>
						<vue-markdown-it
							:source="selectedRecap.content"
							preset="default"
							:options="{ html: true }"
							class="markdown"
						/>
					</div>
				</div>
				<!-- Campaign overview while no mission is open -->
				<div ref="overview" class="logs-overview" v-else>
					<div class="overview-stats">
						<div class="overview-stat">
							<span class="stat-label">Missions completed</span>
							<div class="overview-figures">
								<div class="overview-figure total">
									<span class="overview-value" data-count>{{ completed.length }}</span
									><span>Completed</span>
								</div>
								<div class="overview-figure success">
									<span class="overview-value" data-count>{{ outcomeCounts.success }}</span
									><span>Success</span>
								</div>
								<div class="overview-figure partial-success">
									<span class="overview-value" data-count>{{
										outcomeCounts["partial-success"]
									}}</span
									><span>Partial</span>
								</div>
								<div class="overview-figure failure">
									<span class="overview-value" data-count>{{ outcomeCounts.failure }}</span
									><span>Failure</span>
								</div>
							</div>
						</div>
						<button
							v-if="currentAssignment"
							class="overview-stat current-assignment"
							@click="selectMission(currentAssignment)"
						>
							<span class="stat-label">Current assignment</span>
							<span class="ca-slug">Mission // {{ currentAssignment.slug }}</span>
							<span class="ca-name">{{ currentAssignment.name }}</span>
						</button>
					</div>

					<div class="overview-stat tracker">
						<span class="stat-label">Campaign tracker</span>
						<div class="tracker-body">
							<div class="tracker-grid">
								<div v-for="stat in trackerStats" :key="stat.label" class="tracker-tile">
									<span class="overview-value" data-count>{{ stat.value }}</span>
									<span class="tracker-label">{{ stat.label }}</span>
								</div>
							</div>
							<div class="tracker-chart">
								<StandingChart
									:series="trackerSeries"
									:animate="animate"
									:min="0"
									:max="null"
									:bands="false"
									:signed="false"
									empty-hint="Add dated <code>history</code> entries to <code>campaign-stats.json</code>, or let Foundry file engagements, to chart the tracker over time."
								/>
							</div>
						</div>
					</div>

					<!-- Fights the Foundry module filed when they ended (src/data/liveState.js) -->
					<div v-if="engagements.length" class="overview-stat history engagements">
						<span class="stat-label">Engagement log</span>
						<div
							v-for="e in engagements"
							:key="e.id"
							class="history-row engagement"
							:class="ENGAGEMENT_CLASSES[e.outcome]"
						>
							<span class="history-slug">// {{ e.date.slice(0, 10) }}</span>
							<span class="history-name">{{ e.scene || e.leader || "Engagement" }}</span>
							<span class="history-outcome">{{ engagementSummary(e) }}</span>
						</div>
					</div>

					<div class="overview-stat history">
						<span class="stat-label">Mission history</span>
						<button
							v-for="m in missions"
							:key="m.slug"
							class="history-row"
							:class="m.status"
							@click="selectMission(m)"
						>
							<span class="history-slug">// {{ m.slug }}</span>
							<span class="history-name">{{ m.name }}</span>
							<span class="history-outcome">{{ outcomeLabel(m.status) }}</span>
						</button>
						<div v-if="!missions.length" class="detail-empty"><p>// NO MISSIONS ON FILE //</p></div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import { VueMarkdownIt } from "@f3ve/vue-markdown-it";
import campaignStats from "@/data/info/campaign-stats.json";
import { live } from "@/data/liveState.js";
import StandingChart from "@/components/StandingChart.vue";
import { animate, stagger } from "motion";
import { retrieveRows, revealRecord, countUp, reducedMotion } from "@/utils/retrieve";
import { onEscape } from "@/utils/escape";

const EASE_OUT = [0.22, 1, 0.36, 1];
// A filed engagement's outcome, as a mission history row colour
const ENGAGEMENT_CLASSES = { victory: "success", defeat: "failure", ended: "start" };
const ENGAGEMENT_OUTCOMES = { victory: "Victory", defeat: "Defeat", ended: "Ended" };
// How many filed engagements the overview lists, newest first
const ENGAGEMENTS_SHOWN = 8;

const OUTCOMES = {
	success: "Success",
	"partial-success": "Partial success",
	failure: "Failure",
	start: "In progress",
};

export default {
	components: {
		VueMarkdownIt,
		StandingChart,
	},
	props: {
		animate: {
			type: Boolean,
			required: true,
		},
		missions: {
			type: Array,
			required: true,
		},
		recaps: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			selectedSlug: null,
			animateView: this.animate,
			animationDelay: "0s",
			ENGAGEMENT_CLASSES,
		};
	},
	computed: {
		// Nothing open by default: the campaign overview shows instead
		selectedMission() {
			return this.missions.find(m => m.slug === this.selectedSlug) || null;
		},
		completed() {
			return this.missions.filter(m => m.status in OUTCOMES && m.status !== "start");
		},
		outcomeCounts() {
			const counts = { success: 0, "partial-success": 0, failure: 0 };
			this.completed.forEach(m => counts[m.status] !== undefined && counts[m.status]++);
			return counts;
		},
		// The hand-kept totals plus what filed engagements added
		trackerStats() {
			const stats = (campaignStats.stats || []).map(stat => ({
				...stat,
				value: (Number(stat.value) || 0) + (Number(live.totals[stat.label]) || 0),
			}));
			for (const [label, value] of Object.entries(live.totals)) {
				if (!stats.some(stat => stat.label === label))
					stats.push({ label, value: Number(value) || 0 });
			}
			return stats;
		},
		engagements() {
			return live.engagements.slice(0, ENGAGEMENTS_SHOWN);
		},
		trackerSeries() {
			const palette = ["#C0394F", "#B8973A", "#5B8FD9", "#3B7A57", "#C000B2", "#0092A4"];
			// Filed engagements chart on top of the hand-kept totals
			const base = label =>
				Number((campaignStats.stats || []).find(stat => stat.label === label)?.value) || 0;
			const filed = live.history.map(point => ({
				date: point.date,
				values: Object.fromEntries(
					Object.entries(point.values || {}).map(([label, value]) => [
						label,
						base(label) + (Number(value) || 0),
					])
				),
			}));
			const history = [...(campaignStats.history || []), ...filed]
				.filter(h => h && h.date && h.values)
				.sort((a, b) => String(a.date).localeCompare(String(b.date)));
			const labels = [...new Set(history.flatMap(h => Object.keys(h.values)))];
			return labels.map((label, i) => ({
				name: label,
				color: palette[i % palette.length],
				history: history
					.filter(h => label in h.values)
					.map(h => ({ when: String(h.date), status: Number(h.values[label]) || 0 })),
			}));
		},
		currentAssignment() {
			return this.missions.find(m => m.status === "start") || null;
		},
		selectedRecap() {
			if (!this.selectedMission) return null;
			return this.recaps.find(r => r.slug === this.selectedMission.slug) || null;
		},
	},
	watch: {
		selectedMission() {
			this.$nextTick(() => (this.selectedMission ? this.revealLog() : this.introOverview(0)));
		},
		// Missions load asynchronously; play the overview entrance once they're in
		"missions.length"(n, old) {
			if (n && !old && !this.selectedMission) this.$nextTick(() => this.introOverview(0.5));
		},
	},
	mounted() {
		if (this.missions.length) this.introOverview(0.5);
		// Esc: back from a mission log to the overview
		this.offEscape = onEscape(() =>
			this.selectedSlug ? ((this.selectedSlug = null), true) : false
		);
	},
	beforeUnmount() {
		this.offEscape?.();
	},
	methods: {
		selectMission(mission) {
			// Clicking the open mission again goes back to the overview
			this.selectedSlug = this.selectedSlug === mission.slug ? null : mission.slug;
		},
		outcomeLabel(status) {
			return OUTCOMES[status] || status;
		},
		engagementSummary(e) {
			const hostiles = `${e.hostilesDefeated}/${e.enemies?.length ?? 0} hostiles`;
			return [ENGAGEMENT_OUTCOMES[e.outcome] || e.outcome, `${e.rounds} rounds`, hostiles].join(
				" // "
			);
		},
		// Boxes wipe in, numbers count up, history rows follow one by one
		introOverview(base) {
			const box = this.$refs.overview;
			if (!box || reducedMotion()) return;
			retrieveRows(
				box.querySelectorAll(":scope > .overview-stats > .overview-stat, :scope > .tracker"),
				{ startDelay: base, gap: 0.15, duration: 0.6 }
			);
			box
				.querySelectorAll("[data-count]")
				.forEach((el, i) =>
					countUp(el, Number(el.textContent) || 0, { delay: base + 0.3 + i * 0.04 })
				);
			retrieveRows(box.querySelectorAll(".history-row"), {
				startDelay: base + 0.5,
				gap: 0.12,
				duration: 0.5,
			});
			const tiles = box.querySelectorAll(".tracker-tile");
			if (tiles.length)
				animate(
					tiles,
					{ opacity: [0, 1], y: [10, 0] },
					{ duration: 0.45, delay: stagger(0.08, { startDelay: base + 0.4 }), ease: EASE_OUT }
				);
		},
		revealLog() {
			const detail = this.$refs.detail;
			if (!detail) return;
			const box = detail.closest(".section-content-container");
			if (box) box.scrollTop = 0;
			const md = detail.querySelector(".markdown");
			revealRecord(null, md ? md.children : []);
		},
	},
};
</script>

<style scoped>
.tracker-body {
	display: flex;
	flex-wrap: wrap;
	gap: 1em;
}

.tracker-grid {
	flex: 1 1 420px;
	align-content: start;
}

.tracker-chart {
	flex: 1 1 420px;
	display: flex;
	min-height: 220px;
}

/* ── Campaign overview ── */
.logs-overview {
	display: flex;
	flex-direction: column;
	gap: 1em;
	padding: 1.5em;
}

.logs-overview .overview-stats {
	padding: 0;
}

.overview-figure.total {
	color: var(--text-pilot-value);
}
.overview-figure.success {
	color: #3b7a57;
}
.overview-figure.partial-success {
	color: #b8973a;
}
.overview-figure.failure {
	color: #c0394f;
}

/* A <button> and the row's last child: restate the border that global button and
   ":last-child" rules drop */
.overview-stats > .current-assignment,
.logs-overview > .overview-stat:last-child {
	border-bottom: 1px solid var(--primary-color);
}

.current-assignment {
	flex: 1 1 260px;
	margin: 0;
	border: 1px solid var(--primary-color);
	border-radius: 0;
	background: rgba(26, 26, 31, 0.35);
	text-align: left;
	cursor: pointer;
	font: inherit;
	color: inherit;
	transition: border-color 0.15s, background-color 0.15s;
}

.current-assignment:hover {
	border-color: #b8973a;
	background-color: rgba(184, 151, 58, 0.08);
}

.ca-slug {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.15em;
	text-transform: uppercase;
	color: #b8973a;
}

.ca-name {
	font-family: "Big Shoulders Display", cursive;
	font-size: 30px;
	font-weight: 800;
	line-height: 1.05;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: var(--text-mission-status);
}

.tracker-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
	gap: 0.6em;
}

.tracker-tile {
	display: flex;
	flex-direction: column;
	gap: 0.2em;
	padding: 0.6em 0.8em;
	border: 1px solid rgba(209, 200, 184, 0.12);
	background: rgba(26, 26, 31, 0.4);
	color: var(--text-mission-status);
}

.tracker-label {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--text-pilot-code);
}

/* .overview-stat's 280px flex-basis is meant for rows; in this column it would be a height */
.logs-overview > .overview-stat {
	flex: 0 0 auto;
}

.history {
	gap: 0.3em;
}

.history-row {
	display: grid;
	grid-template-columns: 4.5em 1fr auto;
	align-items: baseline;
	gap: 1em;
	padding: 0.55em 0.8em;
	border: 0;
	border-left: 3px solid rgba(209, 200, 184, 0.3);
	background: rgba(26, 26, 31, 0.35);
	text-align: left;
	cursor: pointer;
	transition: background-color 0.15s;
}

.history-row:hover {
	background: rgba(184, 151, 58, 0.08);
}

/* Engagement rows are a record, not a link; their slug is a date, wider than a mission number */
.history-row.engagement {
	grid-template-columns: 7.5em 1fr auto;
	cursor: default;
}

.history-row.engagement:hover {
	background: rgba(26, 26, 31, 0.35);
}

.history-row.success {
	border-left-color: #3b7a57;
}
.history-row.partial-success {
	border-left-color: #b8973a;
}
.history-row.failure {
	border-left-color: #c0394f;
}
.history-row.start {
	border-left-color: #d1c8b8;
}

.history-slug {
	font-family: "Share Tech Mono", monospace;
	font-size: 12px;
	color: var(--text-pilot-code);
}

.history-name {
	font-family: "Big Shoulders Display", cursive;
	font-size: 18px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: var(--text-mission-status);
}

.history-outcome {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--text-location);
}

.history-row.success .history-outcome {
	color: #3b7a57;
}
.history-row.partial-success .history-outcome {
	color: #b8973a;
}
.history-row.failure .history-outcome {
	color: #c0394f;
}

.log-detail .dossier-back {
	align-self: flex-start;
	margin: 1em 2em 0;
}

.log-detail {
	display: flex;
	flex-direction: column;
	min-height: 100%;
}

.log-detail .markdown {
	height: auto;
	overflow: visible;
	border-bottom: none;
	padding: 0.5em 2em 1em 2em;
}

.recap-section {
	margin: 1em;
	border: 1px solid var(--primary-color);
}

.recap-header {
	display: flex;
	align-items: center;
	gap: 0.5em;
	padding: 0.5em 0.75em;
	background: var(--primary-color);
	font-family: "Big Shoulders Display", cursive;
	font-size: 14px;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.1em;
}

.recap-icon {
	width: 16px;
	height: 16px;
}

.recap-section .markdown {
	padding: 0 1em;
}

.section-content-container.extra-margins {
	display: flex;
	flex-direction: column;
}

.section-content-container.extra-margins > .log-detail,
.section-content-container.extra-margins > .logs-overview,
.section-content-container.extra-margins > .detail-empty {
	flex: 1;
}
</style>
